import { apiClient } from '@/api';

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_EMAIL_KEY = 'admin_email';

const buildUrl = (path: string) => apiClient.buildEndpoint(path);

// ==================== Session management ====================

export function getAdminToken(): string | null {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export function getAdminEmail(): string | null {
  return sessionStorage.getItem(ADMIN_EMAIL_KEY);
}

export function setAdminSession(token: string, email: string) {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  sessionStorage.setItem(ADMIN_EMAIL_KEY, email);
}

export function clearAdminSession() {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_EMAIL_KEY);
}

function adminHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = getAdminToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

// ==================== Fetch wrapper ====================

async function adminFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...adminHeaders(),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401) {
    clearAdminSession();
    window.dispatchEvent(new CustomEvent('admin-session-expired'));
    throw new Error('Session expired');
  }

  return res;
}

// ==================== Types ====================

export interface Thread {
  id: string;
  name: string;
  createdAt: string;
  userIdentifier?: string;
  userId?: string;
  tags: string[];
  metadata: Record<string, unknown>;
}

export interface ThreadDetail extends Thread {
  steps: ThreadStep[];
}

export type ThreadStep = MessageStep | ToolStep;

export interface MessageStep {
  id: string;
  type: 'message';
  role: string;
  content: string;
  createdAt: string;
}

export interface ToolStep {
  id: string;
  type: 'tool' | 'llm' | 'retrieval' | 'rerank' | 'embedding' | 'run';
  name: string;
  input: string;
  output: string;
  isError: boolean;
  parentId?: string;
  createdAt: string;
}

export interface FeedbackEntry {
  id: string;
  threadId?: string;
  stepId?: string;
  value: number;
  comment?: string;
  userIdentifier?: string;
  threadName?: string;
  createdAt: string;
}

export interface DocumentEntry {
  id: string;
  document_name: string;
  source: string;
  url?: string;
  uploadDate?: string;
  chunks_count: number;
}

export interface DailyStat {
  date: string;
  count: number;
}

export interface CostCard {
  key: 'hosting' | 'anthropic' | 'google_embed' | 'qdrant';
  label: string;
  base_usd: number;
  variable_usd: number;
  total_usd: number;
  unit_label: string;
  quantity: number;
  rate_usd: number;
  detail: string;
}

export interface CostStats {
  cards: CostCard[];
  grand_total_usd: number;
  total_queries: number;
  total_embeddings: number;
  stored_vectors: number;
  days_until_reset: number;
  cycle_start_iso: string;
}

export interface DashboardSummary {
  conversations: number;
  questions: number;
  answers: number;
  tool_calls: number;
  tool_errors: number;
  average_response_seconds: number;
  positive_feedback: number;
  negative_feedback: number;
}

export interface DailyInsight {
  date: string;
  questions: number;
  answers: number;
}

export interface ToolInsight {
  name: string;
  count: number;
  errors: number;
}

export interface UnansweredQuestionInsight {
  question: string;
  occurrences: number;
  latest_at: string | null;
}

export interface DashboardInsights {
  period_days: 7 | 30 | 90;
  summary: DashboardSummary;
  daily: DailyInsight[];
  tools: ToolInsight[];
  unanswered: UnansweredQuestionInsight[];
}

export interface SyncResult {
  url: string;
  status: 'success' | 'failed' | 'skipped';
  message?: string;
  document_count?: number;
}

export interface SubPage {
  url: string;
  count: number;
  indexed_date: string;
}

export interface DomainGroup {
  base_domain: string;
  total_documents: number;
  indexed_date: string;
  sub_pages: SubPage[];
}

export interface AdminUser {
  id: string;
  email: string;
  must_change_password: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// ==================== Auth API ====================

export async function adminLogin(
  email: string,
  password: string
): Promise<{ success: boolean; must_change_password?: boolean }> {
  const res = await fetch(buildUrl('/api/admin/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) return { success: false };
  const data = await res.json();
  if (data.success) {
    setAdminSession(data.token, email);
  }
  return { success: data.success, must_change_password: data.must_change_password };
}

export async function changePassword(
  newPassword: string
): Promise<{ success: boolean }> {
  const email = getAdminEmail();
  const res = await adminFetch(buildUrl('/api/admin/change-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_password: newPassword }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to change password');
  }
  const data = await res.json();
  if (data.token && email) {
    setAdminSession(data.token, email);
  }
  return { success: true };
}

// ==================== Admin user management ====================

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const res = await adminFetch(buildUrl('/api/admin/users/list'), {
    method: 'POST',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Failed to fetch admin users (${res.status})`);
  }
  const data = await res.json();
  return data.users;
}

export async function createAdminUser(
  email: string
): Promise<{ user: AdminUser; temp_password: string }> {
  const res = await adminFetch(buildUrl('/api/admin/users'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to create admin user');
  }
  return res.json();
}

export async function deleteAdminUser(userId: string): Promise<void> {
  const res = await adminFetch(
    buildUrl(`/api/admin/users/${encodeURIComponent(userId)}`),
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to delete admin user');
  }
}

// ==================== Data API ====================

export async function fetchThreads(params: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{
  threads: Thread[];
  total: number;
  page: number;
  limit: number;
  has_next_page: boolean;
}> {
  const res = await adminFetch(buildUrl('/api/admin/threads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation: 'get_all_threads',
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      search: params.search || undefined,
    }),
  });
  if (!res.ok) throw new Error('Failed to fetch threads');
  return res.json();
}

export async function fetchThreadDetail(threadId: string): Promise<ThreadDetail> {
  const res = await adminFetch(buildUrl('/api/admin/threads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation: 'get_thread_details',
      thread_id: threadId,
    }),
  });
  if (!res.ok) throw new Error('Failed to fetch thread detail');
  return res.json();
}

export async function deleteThread(threadId: string): Promise<void> {
  const res = await adminFetch(buildUrl('/api/admin/threads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation: 'delete_thread',
      thread_id: threadId,
    }),
  });
  if (!res.ok) throw new Error('Failed to delete thread');
}

export async function fetchFeedback(params: {
  page?: number;
  limit?: number;
}): Promise<{
  feedback: FeedbackEntry[];
  total: number;
  page: number;
  limit: number;
  has_next_page: boolean;
}> {
  const res = await adminFetch(buildUrl('/api/admin/feedback'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      page: params.page ?? 1,
      limit: params.limit ?? 100,
    }),
  });
  if (!res.ok) throw new Error('Failed to fetch feedback');
  return res.json();
}

export async function fetchDocuments(): Promise<{ documents: DocumentEntry[] }> {
  const res = await adminFetch(buildUrl('/api/admin/documents'), {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Failed to fetch documents');
  return res.json();
}

export async function uploadDocuments(files: FileList): Promise<void> {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('files', files[i]);
  }
  const res = await adminFetch(buildUrl('/api/admin/documents/upload'), {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload documents');
}

export async function deleteDocument(documentName: string): Promise<void> {
  const res = await adminFetch(
    buildUrl(`/api/admin/documents/${encodeURIComponent(documentName)}`),
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error('Failed to delete document');
}

export async function fetchDailyStats(
  days = 30
): Promise<{ stats: DailyStat[] }> {
  const res = await adminFetch(buildUrl('/api/admin/threads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation: 'daily_stats',
      days,
    }),
  });
  if (!res.ok) throw new Error('Failed to fetch daily stats');
  return res.json();
}

export async function fetchCostStats(days = 30): Promise<CostStats> {
  const res = await adminFetch(buildUrl('/api/admin/threads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation: 'cost_stats',
      days,
    }),
  });
  if (!res.ok) throw new Error('Failed to fetch cost stats');
  return res.json();
}

export async function fetchDashboardInsights(
  days: 7 | 30 | 90
): Promise<DashboardInsights> {
  const res = await adminFetch(buildUrl('/api/admin/threads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation: 'dashboard_insights', days }),
  });
  if (!res.ok) throw new Error('Failed to fetch dashboard insights');
  return res.json();
}

// ==================== Site Sync API ====================

export async function syncSites(
  urls: string[],
  mode: 'scrape' | 'crawl' = 'scrape'
): Promise<{
  message: string;
  total_documents: number;
  results: SyncResult[];
}> {
  const res = await adminFetch(buildUrl('/api/admin/site-sync'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ urls, mode }),
  });
  if (!res.ok) throw new Error('Failed to sync sites');
  return res.json();
}

export async function fetchGroupedUrls(): Promise<{
  domains: Record<string, DomainGroup>;
  total_domains: number;
}> {
  const res = await adminFetch(buildUrl('/api/admin/site-sync/urls'), {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Failed to fetch indexed sites');
  return res.json();
}

export async function deleteSyncedUrl(url: string): Promise<{
  deleted_count: number;
  message: string;
}> {
  const res = await adminFetch(buildUrl('/api/admin/site-sync/delete-url'), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) throw new Error('Failed to delete URL');
  return res.json();
}

export async function deleteSyncedDomain(domain: string): Promise<{
  deleted_count: number;
  message: string;
}> {
  const res = await adminFetch(buildUrl('/api/admin/site-sync/delete-domain'), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  if (!res.ok) throw new Error('Failed to delete domain');
  return res.json();
}
